import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';

// Enterprise-grade icons
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import GroupsIcon from '@mui/icons-material/Groups';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)',  // Professional blue gradient
  color: 'white',
  padding: theme.spacing(12, 0, 8, 0),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.15)',  // Dark slate overlay
    zIndex: 1,
  },
  '& > *': {
    position: 'relative',
    zIndex: 2,
  }
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const StatsBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  border: `1px solid ${theme.palette.divider}`,
  background: 'rgba(255,255,255,0.9)',
  backdropFilter: 'blur(10px)',
}));

const TrustIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  background: 'rgba(255,255,255,0.8)',
  backdropFilter: 'blur(5px)',
}));

const Home = () => {
  const enterpriseFeatures = [
    {
      icon: <SecurityIcon fontSize="large" color="primary" />,
      title: 'Enterprise Security',
      description: 'SOC 2 compliant with end-to-end encryption, SSO integration, and comprehensive audit logs for complete visibility.',
      badge: 'Security First'
    },
    {
      icon: <GroupsIcon fontSize="large" color="primary" />,
      title: 'Scalable Team Management',
      description: 'Support unlimited teams and projects with role-based access control, advanced permissions, and centralized administration.',
      badge: 'Enterprise Scale'
    },
    {
      icon: <AnalyticsIcon fontSize="large" color="primary" />,
      title: 'Advanced Analytics',
      description: 'Comprehensive reporting dashboard with hill chart progress, cycle throughput, and project insights.',
      badge: 'Data Driven'
    },
    {
      icon: <IntegrationInstructionsIcon fontSize="large" color="primary" />,
      title: 'Seamless Integrations',
      description: 'Native integrations with Jira, Azure DevOps, GitHub, and custom API endpoints for workflow automation.',
      badge: 'API Ready'
    },
    {
      icon: <CloudDoneIcon fontSize="large" color="primary" />,
      title: 'Cloud & On-Premise',
      description: 'Flexible deployment options including cloud-hosted, on-premise, and hybrid configurations to meet your compliance needs.',
      badge: 'Flexible Deployment'
    },
    {
      icon: <BusinessCenterIcon fontSize="large" color="primary" />,
      title: '24/7 Enterprise Support',
      description: 'Dedicated customer success manager, priority support, and SLA guarantees with 99.9% uptime commitment.',
      badge: 'Enterprise Support'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'VP of Product',
      company: 'TechCorp Inc.',
      avatar: 'SC',
      quote: 'PlanningSync changed how our 200+ product teams plan work. Betting on shaped pitches instead of grooming a backlog gives us back weeks every cycle.'
    },
    {
      name: 'Michael Rodriguez',
      role: 'Head of Product Operations',
      company: 'Global Systems Ltd.',
      avatar: 'MR',
      quote: 'The hill charts give us an honest read on where every project really stands — no more guessing from a burndown or a status meeting.'
    },
    {
      name: 'Jennifer Kim',
      role: 'CTO',
      company: 'Innovation Labs',
      avatar: 'JK',
      quote: 'Implementation was seamless with their enterprise support. SSO integration and audit logs check all our compliance boxes.'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Trusted by 500+ Product Teams"
                sx={{ mb: 3, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Shape Up for Product Teams
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, fontWeight: 300 }}>
                Plan pitches, place bets, and track progress on hill charts. Give your teams a
                fixed appetite and the autonomy to ship — without endless backlogs or status meetings.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  component={RouterLink}
                  to="/shape-up"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': { bgcolor: '#e5e5e5' }
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ 
                    px: 4, 
                    py: 1.5, 
                    fontSize: '1.1rem',
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': { borderColor: 'grey.300', bgcolor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  Schedule Demo
                </Button>
              </Stack>
              
              {/* Trust Indicators */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6} sm={3}>
                  <TrustIndicator>
                    <Box textAlign="center">
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                        99.9%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Uptime SLA
                      </Typography>
                    </Box>
                  </TrustIndicator>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TrustIndicator>
                    <Box textAlign="center">
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                        SOC 2
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Compliant
                      </Typography>
                    </Box>
                  </TrustIndicator>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TrustIndicator>
                    <Box textAlign="center">
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                        24/7
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Support
                      </Typography>
                    </Box>
                  </TrustIndicator>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TrustIndicator>
                    <Box textAlign="center">
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                        500+
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Enterprise Teams
                      </Typography>
                    </Box>
                  </TrustIndicator>
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <StatsBox elevation={3}>
                  <TrendingUpIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    35%
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    More projects shipped within their appetite
                  </Typography>
                </StatsBox>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Enterprise Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
            Enterprise-Ready Features
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, fontWeight: 300 }}>
            Everything your organization needs to run Shape Up at scale
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {enterpriseFeatures.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <FeatureCard elevation={2}>
                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {feature.icon}
                    <Chip 
                      label={feature.badge} 
                      size="small" 
                      color="primary" 
                      variant="outlined" 
                      sx={{ ml: 'auto' }} 
                    />
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Social Proof Section */}
      <Box sx={{ bgcolor: '#141414', py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
              Trusted by Industry Leaders
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
              See how product teams are shipping better work with Shape Up
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card elevation={2} sx={{ height: '100%', p: 3 }}>
                  <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic' }}>
                    "{testimonial.quote}"
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      {testimonial.avatar}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {testimonial.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {testimonial.role} at {testimonial.company}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
            How Shape Up Works
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
            Fixed time, variable scope — a proven way to ship meaningful work in six weeks
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box sx={{ 
                  minWidth: 40, 
                  height: 40, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  fontWeight: 700
                }}>
                  1
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Shape the Work
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Set a clear appetite and shape a pitch that defines the problem, the solution, and its boundaries before any work is bet on.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box sx={{ 
                  minWidth: 40, 
                  height: 40, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  fontWeight: 700
                }}>
                  2
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Place Your Bets
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    At the betting table, leadership bets on the pitches worth doing this cycle. If it isn't bet on, it doesn't get built.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box sx={{ 
                  minWidth: 40, 
                  height: 40, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  fontWeight: 700
                }}>
                  3
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Build in a Cycle
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    A small team takes full responsibility for a bet and builds it end-to-end during a focused six-week cycle.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box sx={{ 
                  minWidth: 40, 
                  height: 40, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  fontWeight: 700
                }}>
                  4
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Track on the Hill
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Follow real progress on the hill chart as scopes move from figuring things out (uphill) to getting them done (downhill).
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                Why Teams Switch to Shape Up
              </Typography>
              <Divider sx={{ my: 3, bgcolor: 'rgba(255,255,255,0.3)' }} />
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    40%
                  </Typography>
                  <Typography variant="body2">
                    Fewer projects past deadline
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    60%
                  </Typography>
                  <Typography variant="body2">
                    Less time in status meetings
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    25%
                  </Typography>
                  <Typography variant="body2">
                    Better team alignment
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    85%
                  </Typography>
                  <Typography variant="body2">
                    Team satisfaction
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 8 }}>
        <Container maxWidth="lg" textAlign="center">
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
            Ready to Ship Work That Matters?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.8 }}>
            Join 500+ product teams already planning with Shape Up
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/shape-up"
              sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
            >
              Start Free 30-Day Trial
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{ 
                px: 4, 
                py: 1.5, 
                fontSize: '1.1rem',
                borderColor: 'white',
                color: 'white',
                '&:hover': { borderColor: 'grey.300', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Contact Sales
            </Button>
          </Stack>
          
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              No credit card required • Setup in under 5 minutes • Cancel anytime
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Home;
